import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Cpu, Loader2, Play, Plus, Save, Sparkles } from "lucide-react";
import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { QaQuestionEditor } from "@/components/agent-qa/QaQuestionEditor";
import { QaResultCard } from "@/components/agent-qa/QaResultCard";
import { agentLlmGet, generateAgentQa, getAgent, getAgentQa, runAgentQa, saveAgentQa } from "@/lib/api";

const errMsg = (e, f) => e?.response?.data?.detail || f;
const btn = "rounded-sm text-xs uppercase tracking-[0.18em]";

const LlmBadge = ({ llm }) => {
  const eff = llm?.effective;
  if (!eff) return null;
  // Fix 7 (N11): client sources (client_agent / client_company / blocked) have their own label
  const SRC = { inviktus: "modelo por defecto", agent: "configurado para este agente", global: "configurado por el admin",
    client_agent: "clave del agente · empresa cliente", client_company: "clave genérica · empresa cliente" };
  const bad = eff.source === "error" || eff.source === "blocked";
  const label = bad
    ? (eff.error || "Sin clave utilizable de la empresa o límite mensual alcanzado.")
    : `${eff.provider} · ${eff.model} (${SRC[eff.source] || eff.source})`;
  const isClient = ["client_agent", "client_company", "blocked"].includes(eff.source);
  return (
    <>
      <p className={`mt-3 inline-flex items-center gap-2 border px-3 py-1.5 text-xs ${bad ? "border-red-400 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-700"}`} data-testid="qa-llm-badge">
        <Cpu className="h-3.5 w-3.5 text-accent-blue" /> LLM: {label}
      </p>
      {isClient && (
        <p className="mt-2 text-xs text-amber-800" data-testid="qa-client-cost">
          Agente de una empresa cliente: ejecutar las pruebas consume la clave y el límite mensual de tokens del cliente.
        </p>
      )}
    </>
  );
};

export default function AgentQa() {
  const { slug } = useParams();
  const [agent, setAgent] = useState(null);
  const [llm, setLlm] = useState(null);
  const [qa, setQa] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    const d = await getAgentQa(slug);
    setQa(d);
    return d;
  }, [slug]);

  useEffect(() => {
    getAgent(slug).then((d) => setAgent(d.agent)).catch(() => {});
    agentLlmGet(slug).then(setLlm).catch(() => {});
    refresh().then((d) => setQuestions(d.questions)).catch((e) => toast.error(errMsg(e, "No se pudieron leer las pruebas.")));
  }, [slug, refresh]);

  const running = qa?.run?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [running, refresh]);

  const act = async (kind, fn, ok) => {
    setBusy(kind);
    try {
      const d = await fn();
      setQa(d);
      setQuestions(d.questions);
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(errMsg(e, "La operación falló."));
    } finally {
      setBusy(null);
    }
  };

  const generate = () => {
    if (questions.length && !window.confirm("Se reemplazarán las preguntas actuales. ¿Continuar?")) return;
    act("generate", () => generateAgentQa(slug), "Preguntas generadas desde la KBL del agente.");
  };
  const save = () => act("save", () => saveAgentQa(slug, questions), "Preguntas guardadas.");
  const run = () => act("run", async () => { await saveAgentQa(slug, questions); return runAgentQa(slug); });
  const update = (i, q) => setQuestions((qs) => qs.map((x, j) => (j === i ? q : x)));
  const remove = (i) => setQuestions((qs) => qs.filter((_, j) => j !== i));
  const add = () => setQuestions((qs) => [...qs, { question: "", expected_facts: [], source_doc: "" }]);

  const r = qa?.run;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="agent-qa-page">
      <TopNav />
      <main className="mx-auto max-w-6xl px-6 py-12 lg:px-10">
        <Link to={`/agentes/${slug}/editar`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900" data-testid="qa-back-link">
          <ArrowLeft className="h-3 w-3" /> Editar agente
        </Link>
        <p className="eyebrow mt-6 text-accent-blue">Pruebas de control · KBL + RAG</p>
        <h1 className="mt-3 font-display text-4xl font-medium sm:text-5xl">{agent?.name || "Agente"}</h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Preguntas basadas en los {qa?.kbl_docs?.length ?? "…"} documentos KBL de {agent?.department || "su departamento"}. Cada
          respuesta se aprueba si cita su propia KBL, contiene los datos clave y la revisión por IA la valida.
        </p>
        <LlmBadge llm={llm} />

        <div className="mt-8 flex flex-wrap gap-3">
          <Button onClick={generate} disabled={!!busy || running} variant="outline" className={`${btn} border-slate-900 bg-white text-slate-900`} data-testid="qa-generate-btn">
            {busy === "generate" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Generar con IA
          </Button>
          <Button onClick={add} disabled={!!busy || running} variant="outline" className={`${btn} border-slate-300 bg-white text-slate-700`} data-testid="qa-add-btn">
            <Plus className="mr-2 h-4 w-4" /> Agregar pregunta
          </Button>
          <Button onClick={save} disabled={!!busy || running} variant="outline" className={`${btn} border-slate-300 bg-white text-slate-700`} data-testid="qa-save-btn">
            <Save className="mr-2 h-4 w-4" /> Guardar
          </Button>
          <Button onClick={run} disabled={!!busy || running || !questions.length} className={`${btn} bg-slate-900 text-white hover:bg-slate-800`} data-testid="qa-run-btn">
            {running || busy === "run" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Ejecutar pruebas
          </Button>
        </div>

        <ol className="mt-6 space-y-3" data-testid="qa-questions-list">
          {questions.map((q, i) => (
            <QaQuestionEditor key={q.id || `new-${i}`} q={q} index={i} docs={qa?.kbl_docs || []} onChange={(nq) => update(i, nq)} onDelete={() => remove(i)} />
          ))}
          {!questions.length && (
            <li className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500" data-testid="qa-empty">
              Pulse «Generar con IA» para crear preguntas a partir de la KBL del agente, o agréguelas a mano.
            </li>
          )}
        </ol>

        {r && (
          <section className="mt-12" data-testid="qa-results">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 className="font-display text-2xl">Resultado de la última prueba</h2>
              <p className="font-mono text-sm" data-testid="qa-score">
                {running ? `Ejecutando ${Math.min(r.progress + 1, r.total)} de ${r.total}…` : `${r.passed} de ${r.total} aprobadas`}
              </p>
            </div>
            {running && (
              <div className="mt-3 h-1.5 bg-slate-200">
                <div className="h-full bg-accent-blue transition-all duration-500" style={{ width: `${(r.progress / Math.max(1, r.total)) * 100}%` }} />
              </div>
            )}
            {r.error && <p className="mt-3 text-sm text-red-700">{r.error}</p>}
            <ol className="mt-4 space-y-3">
              {(r.results || []).map((x, i) => <QaResultCard key={x.id} r={x} index={i} />)}
            </ol>
          </section>
        )}
      </main>
    </div>
  );
}
